/**
 * AppImage 컴포넌트
 * @description 앱 표준 이미지. 리사이즈 · 캐시 정책을 처리하고 기본으로 부모를 채운다.
 *   디자인 시스템의 `Media` 가 비율만 잡고 이미지를 렌더하지 않는 이유는 이미지 최적화가
 *   앱 인프라(`next/image` 의 remotePatterns · 버킷 호스트) 지식이라 DS 가 알 수 없기
 *   때문이다. 앱 `shared/ui/AppImage` 의 웹 포팅 — imgproxy 자리에 `next/image` 가 선다.
 * @example
 * <Media ratio='square' slot={<AppImage uri={post.thumbnailUrl} fallback={IMAGE_PLACEHOLDER} />} />
 */
import Image from "next/image";
import { memo } from "react";

import { appImageStyles } from "./AppImage.styles";
import type { AppImageProps } from "./AppImage.types";

/** 폭 힌트가 없을 때 — 폰 화면 한 폭으로 본다 */
const DEFAULT_SIZES = "100vw";

const AppImage = memo<AppImageProps>(
  ({
    uri,
    fallback,
    altText = "",
    resizeMode,
    targetWidth,
    sizes,
    priority,
    testID,
  }) => {
    // 빈 문자열도 "없음"이다 — 서버가 값을 안 줄 때 `""` 로 오는 자리가 있다
    const src = uri.trim() || fallback;
    if (!src) return null;

    return (
      <Image
        src={src}
        alt={altText}
        fill
        sizes={sizes ?? (targetWidth ? `${targetWidth}px` : DEFAULT_SIZES)}
        priority={priority}
        draggable={false}
        className={appImageStyles({ resizeMode })}
        data-testid={testID}
      />
    );
  },
);

AppImage.displayName = "AppImage";

export default AppImage;
